import { useEffect, useRef, useState } from 'react';

const COARSE_OR_NO_HOVER = '(hover: none), (pointer: coarse)';

/**
 * Shared matchMedia subscription — one native listener for the whole app regardless of how many
 * `<Label>` / `<HotelCancellationLabel>` instances mount. Each hook caller subscribes via a local
 * setter; Safari <14 `addListener` fallback kept. The listener lives for the app lifetime and is
 * intentionally never torn down.
 */
let sharedMql = null;
let sharedTapMode = false;
const tapModeSubscribers = new Set();

const ensureSharedMql = () => {
  if (sharedMql || typeof window === 'undefined' || !window.matchMedia) return;
  sharedMql = window.matchMedia(COARSE_OR_NO_HOVER);
  sharedTapMode = sharedMql.matches;
  const onChange = () => {
    sharedTapMode = sharedMql.matches;
    tapModeSubscribers.forEach((fn) => fn(sharedTapMode));
  };
  if (sharedMql.addEventListener) sharedMql.addEventListener('change', onChange);
  else sharedMql.addListener(onChange);
};

/**
 * MUI Tooltip is hover-oriented; on touch / coarse pointers use tap to toggle and tap-outside to close.
 */
export default function useTapOrCoarsePointerTooltip() {
  /** Always `false` on first render so SSR markup matches client hydration; the real value is
   *  picked up in `useEffect` below. Touch devices flip to `true` on the next commit. */
  const [tapMode, setTapMode] = useState(false);

  useEffect(() => {
    ensureSharedMql();
    tapModeSubscribers.add(setTapMode);
    if (sharedTapMode) setTapMode(true);

    return () => {
      tapModeSubscribers.delete(setTapMode);
    };
  }, []);

  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!tapMode || !open) return undefined;

    /** Portal-safe outside check: MUI Tooltip renders content with `role="tooltip"` outside the
     *  trigger's DOM subtree, so `triggerRef.contains` alone closes the tooltip on any tap inside
     *  its own content. Also skip taps inside `.MuiTooltip-popper` for older MUI without role. */
    const closeIfOutside = (e) => {
      const target = e.target;
      if (triggerRef.current?.contains(target)) return;
      if (target?.closest?.('[role="tooltip"], .MuiTooltip-popper')) return;
      setOpen(false);
    };

    document.addEventListener('click', closeIfOutside, true);
    document.addEventListener('touchstart', closeIfOutside, true);

    return () => {
      document.removeEventListener('click', closeIfOutside, true);
      document.removeEventListener('touchstart', closeIfOutside, true);
    };
  }, [tapMode, open]);

  const tooltipProps = tapMode
    ? {
        open,
        onClose: () => setOpen(false),
        disableHoverListener: true,
        disableFocusListener: true,
        disableTouchListener: true,
      }
    : {};

  const triggerBindings = tapMode
    ? {
        ref: triggerRef,
        role: 'button',
        tabIndex: 0,
        onClick: (e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        },
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        },
      }
    : {};

  return { tapMode, tooltipProps, triggerBindings };
}
